import { useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { regular } from "@fortawesome/fontawesome-svg-core/import.macro";
import { getStepData } from "pages/application/question/questionSlice";
import "./navbarDropDown.scss";
import HeaderMenu from "../header-menu/HeaderMenu";
import { useSelector } from "react-redux";
import useDesktopChecker from "hooks/useDesktopChecker";
import { INavBarProps, NavLinks } from "./NavBarUtilities";
import { NotificationResponse, NotificationType, useGetNotificationDetailsQuery, useSetEsignStatusFlagOfNotificationMutation } from "pages/authentication/userService";
import { getCurrentUserId, getNhDealApplicationId } from "features/authSlice";
import { useGetFeatureFlagsQuery } from "services/common/featureFlagService";
import NotificationPopup from "components/notification-popup/NotificationPopup";
import newNotificationDesktop from "assets/images/notification_new_desktop.svg";
import notificationDesktop from "assets/images/notification_desktop.svg";
import newNotificationMobile from "assets/images/notification_new_mob.svg";
import notificationMobile from "assets/images/notification_mob.svg";
import { useGetDashboardItemsQuery } from "pages/dashboard/home/dashboardService";
import { useEnterMyLoansWithLangPref } from "hooks/useEnterMyLoansWithLangPref";

export default function NavbarDropDown({ userName, showNavLinks = false, showPreferences }: Readonly<INavBarProps>) {
  const navRef = useRef<HTMLElement>(null);
  const [showMenu, setShowMenu] = useState(false);
  const { data: featureFlag } = useGetFeatureFlagsQuery();
  const showNavbar = () => {
    setShowMenu(prev => !prev);
  };
  const isDesktop = useDesktopChecker();
  const stepData = useSelector(getStepData);
  const isSummary = stepData?.stepType === "summary";
  const isHideMyApp = location.pathname === "/my-loans" || location.pathname === "/path";
  const [showNotificationPopupComponent, setshowNotificationPopupComponent] = useState(false);
  const userId = useSelector(getCurrentUserId);
  const nhDealApplicationId = useSelector(getNhDealApplicationId);
  const { data } = useGetDashboardItemsQuery({
    dealApplicationId: nhDealApplicationId,
    userId: userId,
  });
  const loanstatus = data?.loanStatus.status;
  const isLoanStatusFunded = loanstatus == "Funded";
  const goToMyLoans = useEnterMyLoansWithLangPref();
  const notificationIconRef = useRef<HTMLButtonElement>(null);
  const { data: notificationData } = useGetNotificationDetailsQuery(userId, {
    skip: !featureFlag?.preApprovalLetterEnabled,
  });

  const [notifications, setNotifications] = useState<NotificationResponse[] | undefined>([]);
  useEffect(() => {
    if (!notificationData || !nhDealApplicationId) {
      setNotifications([]);
      return;
    }
  
    setNotifications(
      notificationData.filter(
        ({ nhDealApplicationId: id }) =>
          id == null || id === nhDealApplicationId
      )
    );
  }, [notificationData, nhDealApplicationId]);

  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      if (notificationIconRef.current && !notificationIconRef.current.contains(event.target as Node)) {
        setshowNotificationPopupComponent(false);
      }
    };
    document.addEventListener("click", handleDocumentClick);
    return () => {
      document.removeEventListener("click", handleDocumentClick);
    };
  }, []);

  const [esignViewed, setEsignViewed] = useState(false);
  const unviewedEsignNotifications =
    notifications?.filter(
      (notification) => notification.notificationType?.toLowerCase() === NotificationType.Esign.toLowerCase() && !notification.isEsignViewed
    ) ?? [];
  const isUnviewedEsignNotification = unviewedEsignNotifications.length > 0 && !esignViewed;
  const isYetToViewNotification = notifications ? notifications?.length !== 0 || !notifications : false;
  const nonEsignViewNotification = notifications?.find(
    (notification) => notification.notificationType?.toLowerCase() !== NotificationType.Esign.toLowerCase()
  );
  const [setEsignStatusFlagOfNotification] = useSetEsignStatusFlagOfNotificationMutation();

  const handleIconClick = async () => {
    setshowNotificationPopupComponent((prevState) => !prevState);
    if (isUnviewedEsignNotification) {
      // Update all unviewed Esign notifications
      for (const notification of unviewedEsignNotifications) {
        setEsignViewed(true);
        if (notification?.id) {
          await setEsignStatusFlagOfNotification(notification.id);
        }
      }
    }
  };

  const renderNotificationIcon = (variant: "desktop" | "mobile") => {
    const hasNewNotification = isUnviewedEsignNotification || nonEsignViewNotification;
    if (hasNewNotification) {
      return (
        <img
          src={variant === "desktop" ? newNotificationDesktop : newNotificationMobile}
          alt="New notification"
        />
      );
    }

    return (
      <img
        src={variant === "desktop" ? notificationDesktop : notificationMobile}
        alt="Notification"
      />
    );
  };

  return (
    <div className="navbar-container">
      {isDesktop ? (
        <nav ref={navRef} className="navbar navbar--desktop-view">
          {!isSummary && showNavLinks && <NavLinks />}
          <div className="navbar-right">
            {!isHideMyApp && featureFlag?.preApprovalLetterEnabled && !isLoanStatusFunded && (
              <>
                <button type="button" className={`bell-icon ${isYetToViewNotification ? "pointer-on-hover" : ""}`} ref={notificationIconRef} onClick={handleIconClick} tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') void handleIconClick(); }}>
                  {renderNotificationIcon("desktop")}
                </button>
                {showNotificationPopupComponent && isYetToViewNotification && (
                  <NotificationPopup notifications={notifications} setNotifications={setNotifications} />
                )}
              </>
            )}
            {!isHideMyApp && (
              <>
                <NavLink
                  to="/my-loans"
                  state={{ previousPath: location.pathname }}
                  replace
                  className="menu-link"
                  end
                  onClick={goToMyLoans}
                >
                  My Loans
                </NavLink>
                <span className="menu-link-splitter">|</span>
              </>
            )}

            {userName && <HeaderMenu name={userName} showNavbar={showNavbar} showPreferences={showPreferences ?? false} />}
            <button type="button" onClick={showNavbar} className="navbar__btn navbar__close-btn">
              <FontAwesomeIcon icon={regular("xmark")} />
            </button>
          </div>
        </nav>
      ) : (
        <>
          {showMenu && (
            <nav ref={navRef} className="navbar">
              <HeaderMenu onShow={showMenu} showNavbar={showNavbar} showPreferences={showPreferences ?? false} />
              <button type="button" onClick={showNavbar} className="navbar__btn navbar__close-btn">
                <FontAwesomeIcon icon={regular("xmark")} />
              </button>
            </nav>
          )}
        </>
      )}

      {!isDesktop && (
        <div className="navbar__block">
          {!isHideMyApp && featureFlag?.preApprovalLetterEnabled && !isLoanStatusFunded && (
            <>
                <button type="button" className={`bell-icon ${isYetToViewNotification ? "pointer-on-hover" : ""}`} ref={notificationIconRef} onClick={handleIconClick} tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') void handleIconClick(); }}>
                {renderNotificationIcon("mobile")}
              </button>
              {showNotificationPopupComponent && isYetToViewNotification && (
                <NotificationPopup notifications={notifications} setNotifications={setNotifications} />
              )}
            </>
          )}
          <button type="button" onClick={showNavbar} className="navbar__btn">
            <FontAwesomeIcon icon={regular("bars")} />
          </button>
        </div>
      )}
    </div>
  );
}
